/**
 * SubcontractorSMSSection
 * Shows SMS history for a single subcontractor
 */

import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { MessageSquare, Send, RefreshCw, ArrowUpRight, ArrowDownLeft } from 'lucide-react'
import { format } from 'date-fns'
import { supabase } from '../../lib/supabase'
import { SendSMSModal } from '../SendSMSModal'

interface SubcontractorSMSSectionProps {
  assignmentId: string
  subcontractorId: string
  subcontractorName: string
  subcontractorPhone: string | null
  compact?: boolean
}

interface SMSMessage {
  id: string
  message: string
  direction: 'outbound' | 'inbound'
  status: string
  sender_name: string | null
  created_at: string
}

export function SubcontractorSMSSection({
  assignmentId,
  subcontractorId,
  subcontractorName,
  subcontractorPhone,
  compact = false
}: SubcontractorSMSSectionProps) {
  const queryClient = useQueryClient()
  const [showSendModal, setShowSendModal] = useState(false)
  const [expanded, setExpanded] = useState(!compact)

  // Fetch subcontractor SMS for this assignment
  const { data: messages = [], isLoading, refetch } = useQuery({
    queryKey: ['subcontractor-sms', assignmentId, subcontractorId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('communications')
        .select('id, message, direction, status, sender_name, created_at')
        .eq('recipient_id', subcontractorId)
        .eq('recipient_type', 'subcontractor')
        .eq('assignment_id', assignmentId)
        .order('created_at', { ascending: false })
        .limit(20)

      if (error) {
        console.error('Error fetching subcontractor SMS:', error)
        return []
      }

      return data as SMSMessage[]
    },
    enabled: !!subcontractorId && expanded
  })

  const handleMessageSent = () => {
    refetch()
    queryClient.invalidateQueries({ queryKey: ['subcontractor-sms'] })
  }

  // Compact mode - just show count and send button
  if (compact && !expanded) {
    return (
      <div className="flex items-center justify-between py-2 border-t border-nature-200 dark:border-nature-600">
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="text-sm text-nature-500 hover:text-nature-700 dark:hover:text-nature-300 flex items-center gap-1"
        >
          <MessageSquare className="h-3.5 w-3.5" />
          SMS-historik
        </button>
        {subcontractorPhone && (
          <button
            type="button"
            onClick={() => setShowSendModal(true)}
            className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center gap-1"
          >
            <Send className="h-3.5 w-3.5" />
            Skicka
          </button>
        )}

        {showSendModal && subcontractorPhone && (
          <SendSMSModal
            isOpen={showSendModal}
            onClose={() => setShowSendModal(false)}
            recipientType="subcontractor"
            recipientId={subcontractorId}
            recipientName={subcontractorName}
            recipientPhone={subcontractorPhone}
            assignmentId={assignmentId}
            onMessageSent={handleMessageSent}
          />
        )}
      </div>
    )
  }

  return (
    <div className={compact ? 'border-t border-nature-200 dark:border-nature-600 pt-3 mt-3' : ''}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-nature-400" />
          <span className="text-sm font-medium text-nature-700 dark:text-nature-300">
            SMS
          </span>
          {messages.length > 0 && (
            <span className="bg-nature-100 dark:bg-nature-700 text-nature-600 dark:text-nature-400 px-1.5 py-0.5 rounded text-xs">
              {messages.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {compact && (
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="text-xs text-nature-400 hover:text-nature-600"
            >
              Dölj
            </button>
          )}
          <button
            type="button"
            onClick={() => refetch()}
            className="text-nature-400 hover:text-nature-600 dark:hover:text-nature-300"
            title="Uppdatera"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
          {subcontractorPhone && (
            <button
              type="button"
              onClick={() => setShowSendModal(true)}
              className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center gap-1"
            >
              <Send className="h-3.5 w-3.5" />
              Skicka
            </button>
          )}
        </div>
      </div>

      {/* Messages list */}
      {isLoading ? (
        <div className="text-center py-2 text-sm text-nature-500">Laddar...</div>
      ) : messages.length === 0 ? (
        <p className="text-sm text-nature-400 dark:text-nature-500 text-center py-2">
          Inga SMS för detta uppdrag
        </p>
      ) : (
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`p-2 rounded text-sm ${
                msg.direction === 'outbound'
                  ? 'bg-primary-50 dark:bg-primary-900/20'
                  : 'bg-nature-50 dark:bg-nature-700'
              }`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-1">
                  {msg.direction === 'outbound' ? (
                    <ArrowUpRight className="h-3 w-3 text-primary-500" />
                  ) : (
                    <ArrowDownLeft className="h-3 w-3 text-green-500" />
                  )}
                  <span className="text-xs text-nature-500">
                    {msg.direction === 'outbound' ? 'Skickat' : 'Svar'}
                  </span>
                </div>
                <span className="text-xs text-nature-400">
                  {format(new Date(msg.created_at), 'MM-dd HH:mm')}
                </span>
              </div>
              <p className="text-nature-700 dark:text-nature-300 text-xs whitespace-pre-wrap line-clamp-3">
                {msg.message}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Send SMS Modal */}
      {showSendModal && subcontractorPhone && (
        <SendSMSModal
          isOpen={showSendModal}
          onClose={() => setShowSendModal(false)}
          recipientType="subcontractor"
          recipientId={subcontractorId}
          recipientName={subcontractorName}
          recipientPhone={subcontractorPhone}
          assignmentId={assignmentId}
          onMessageSent={handleMessageSent}
        />
      )}
    </div>
  )
}
